import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import {
  GetExpenses, AddExpense, UpdateExpense, GetExpensePayments, AddExpensePayment, UpdateExpensePayment,
  GetServicesByEventId, GetServicePayments, AddServicePayment, UpdateServicePayment, AddCostLine, UpdateCostLine,
} from '../../api/api';
import { normaliseText, todayISO, tenDigits, EXPENSE_CATEGORIES } from '../../utils/helpers';
import { useEventCosts } from '../../utils/useEventCosts';
import {
  blankLine, lineMath, linesTotal, lineToForm, saveLines, isDueSoon, EXPENSE_STATUSES, parentMoney, isQuotation,
} from '../../utils/costLines';

// Expenses of an event — all logic, shared by Expenses.desktop / .mobile.
//   An expense is Fixed (a typed amount) or PerPerson (rate × persons, the
//   persons typed or taken LIVE from a function's headcount, extra-card guests
//   included). It stays an Estimate until the final amount is known.
//   Or it is ITEMISED (AmountType 'Lines'): a booking made of lines - rooms x
//   rate x nights, a hall rent, a dinner for at least N people that follows the
//   function's headcount - priced live by utils/costLines.js. A Service can
//   carry lines too (its detail page); its total here is then the live one.
//   Status (Planned / Ordered / Booked / Done), Needed by and Balance due by
//   drive the "due in 7 days" and "not ordered yet" views.
//   Payments (date, amount, paid by, mode) are kept per expense; Paid comes from
//   the API (sum of payments), Balance = total − paid.
//   The engagements of the SERVICES page (caterers, event managers …) are
//   listed here too, read-only (fromService: true), so every cost of the event
//   is in one place. Their PAYMENTS can be added / edited / deleted right here
//   (Evt_ServicePayments; the API re-totals the service after each change);
//   the service itself (rate, count, vendor) is changed on the Services page.
// Rights: admins always; members by CanExpenses / Add / Edit / Delete.

const blankExpense = () => ({
  Category: '', Title: '', FunctionId: '', VendorName: '', VendorMobile: '',
  AmountType: 'Fixed', Rate: '', Persons: '', PersonsSource: 'Manual', PersonsFunctionId: '',
  TotalAmount: '', IsEstimate: 1, Notes: '', Status: 'Planned', NeededBy: '', DueDate: '', lines: [],
});
const blankPayment = (ExpenseId, PaidBy) => ({
  ExpenseId, PaymentDate: todayISO(), Amount: '', PaidBy: PaidBy || '', PaymentMode: 'Cash', Notes: '',
});
const num = (v) => Number(v || 0) || 0;
const round2 = (n) => Math.round(n * 100) / 100;

export function useExpenses(event) {
  const { user, isAdmin, can } = useAuth();
  const toast = useToast();
  const me = user.UserId;
  const myName = [user.Name, user.Surname].filter(Boolean).join(' ');

  const canView = isAdmin || can('CanExpenses');
  const canAdd = isAdmin || can('CanExpensesAdd');
  const canEdit = isAdmin || can('CanExpensesEdit');
  const canDelete = isAdmin || can('CanExpensesDelete');

  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const costs = useEventCosts(event, canView);
  const { functions, count, linesFor, headcountOf, fnName, reloadLines } = costs;
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [onlyDue, setOnlyDue] = useState(false);
  // '' = everything, 'dueSoon' = balance due within 7 days (or overdue), 'notOrdered' = still Planned
  const [params] = useSearchParams();
  const [stage, setStage] = useState(() => (params.get('due') ? 'dueSoon' : ''));
  const today = todayISO();
  const [openId, setOpenId] = useState(null);       // expense whose payments are shown
  const [form, setForm] = useState(null);           // expense being added / edited
  const [payForm, setPayForm] = useState(null);     // payment being added / edited
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);       // Services page engagements (+ their payments)
  const nav = useNavigate();

  const load = useCallback(async () => {
    if (!canView) { setLoading(false); return; }
    setLoading(true);
    try {
      const [e, p] = await Promise.all([GetExpenses({ EventId: event.EventId }), GetExpensePayments({ EventId: event.EventId })]);
      setExpenses(Array.isArray(e) ? e : []);
      setPayments(Array.isArray(p) ? p : []);
    } catch {
      toast('Could not load expenses');
    } finally {
      setLoading(false);
    }
  }, [event, canView, toast]);

  useEffect(() => { load(); }, [load]);

  // Services (catering etc.) with their payments - shown as expense lines.
  const loadServices = useCallback(async () => {
    if (!canView) return;
    try {
      const list = await GetServicesByEventId({ EventId: event.EventId });
      const live = (Array.isArray(list) ? list : []).filter(x => Number(x.Valid ?? 1) !== 0 && x.Status !== 'Cancelled');
      const withPays = await Promise.all(live.map(async sv => {
        const p = await GetServicePayments({ ServiceId: sv.ServiceId }).catch(() => []);
        return { ...sv, payments: (Array.isArray(p) ? p : []).filter(x => Number(x.Valid ?? 1) !== 0) };
      }));
      setServices(withPays);
    } catch { setServices([]); }
  }, [event, canView]);
  useEffect(() => { loadServices(); }, [loadServices]);

  // Persons / total of an expense (row or form values).
  const personsOf = useCallback((e) => (e.PersonsSource === 'Headcount' && e.PersonsFunctionId
    ? headcountOf(e.PersonsFunctionId) : num(e.Persons)), [headcountOf]);
  const totalOf = useCallback((e) => (e.AmountType === 'Lines'
    ? linesTotal(e.lines || [], count)
    : e.AmountType === 'PerPerson' ? round2(num(e.Rate) * personsOf(e)) : num(e.TotalAmount)), [personsOf, count]);
  // Saved lines with their live numbers, for the lists.
  const withMath = useCallback((ls) => ls.map(l => ({ ...l, m: lineMath(l, count) })), [count]);

  const rows = useMemo(() => {
    const own = expenses.map(e => {
      const lines = withMath(linesFor('Expense', e.ExpenseId));
      const itemised = e.AmountType === 'Lines' || lines.length > 0;
      // No lines loaded (an API without them yet): the total as last saved.
      const total = lines.length ? linesTotal(lines, count) : itemised ? num(e.TotalAmount) : totalOf(e);
      const paid = num(e.PaidAmount);
      const balance = round2(total - paid);
      return {
        ...e, total, paid, balance, persons: personsOf(e), lines, itemised,
        dueSoon: isDueSoon(e.DueDate, balance, today),
        payments: payments.filter(p => String(p.ExpenseId) === String(e.ExpenseId)),
      };
    });
    const fromServices = services.map(sv => {
      const pays = sv.payments.map(p => {
        // "Paid by X · notes" is how the paid-by name is kept in Remarks.
        const m = String(p.Remarks || '').match(/^Paid by ([^·]+?)(?:\s*·\s*(.*))?$/);
        return {
          ExpensePaymentId: `sp${p.PaymentId}`, PaymentId: p.PaymentId, ServiceId: sv.ServiceId,
          PaymentDate: p.PaymentDate, Amount: p.Amount, PaymentMode: p.PaymentMode,
          PaidBy: m ? m[1].trim() : null,
          Notes: [p.Reference, m ? m[2] : p.Remarks].filter(Boolean).join(' · ') || null,
        };
      });
      const lines = withMath(linesFor('Service', sv.ServiceId));
      // Quoted, less the discount, agreed - and what it comes to with every
      // "if needed" line taken and every range at its ceiling.
      const svMoney = parentMoney(sv, lines.length ? lines : null, count, num(sv.TotalAmount));
      const total = svMoney.total;
      // AdvancePaid is kept equal to the sum of the payments by the API.
      const paid = num(sv.AdvancePaid);
      const perPlate = sv.RateType === 'PerPlate';
      return {
        lines, itemised: lines.length > 0, DueDate: sv.DueDate || null,
        dueSoon: isDueSoon(sv.DueDate, round2(total - paid), today),
        NeededBy: sv.ServiceDate || sv.FunctionDate || null,
        fromService: true, ServiceId: sv.ServiceId, ExpenseId: `s${sv.ServiceId}`,
        Category: sv.ServiceType || 'Services', Title: sv.CompanyName || sv.ContactName || sv.ServiceType || 'Service',
        FunctionId: sv.FunctionId || null, VendorName: sv.ContactName || null, VendorMobile: sv.ContactMobile || null,
        Notes: sv.Notes || null, AmountType: perPlate ? 'PerPerson' : 'Fixed', Rate: num(sv.Rate),
        PersonsSource: 'Manual', persons: num(sv.ConfirmedCount),
        IsEstimate: sv.Status === 'Enquiry' ? 1 : 0, Status: sv.Status,
        // A PRICE SOMEBODY SENT is not money owed: an Enquiry is listed and
        // totalled on its own line, never in the balance to pay.
        quotation: isQuotation(sv), money: svMoney,
        total, paid, balance: round2(total - paid),
        LastPaymentDate: pays.map(p => String(p.PaymentDate || '').slice(0, 10)).sort().pop() || null,
        payments: pays,
      };
    });
    return [...own, ...fromServices];
  }, [expenses, payments, services, totalOf, personsOf, linesFor, withMath, count, today]);
  // A service line is changed (and paid) on the Services page.
  const openService = (row) => nav(`/app/services/${row.ServiceId}`);

  const filtered = useMemo(() => {
    let list = rows;
    if (catFilter) list = list.filter(e => e.Category === catFilter);
    if (onlyDue) list = list.filter(e => e.balance > 0);
    if (stage === 'dueSoon') list = list.filter(e => e.dueSoon);
    if (stage === 'notOrdered') list = list.filter(e => !e.fromService && (e.Status || 'Planned') === 'Planned');
    if (q) {
      const s = q.toLowerCase();
      list = list.filter(e => `${e.Title} ${e.Category} ${e.VendorName || ''} ${e.Notes || ''}`.toLowerCase().includes(s));
    }
    return list;
  }, [rows, catFilter, onlyDue, stage, q]);

  // Grouped by category, in the order of EXPENSE_CATEGORIES then any others.
  const groups = useMemo(() => {
    const m = new Map();
    filtered.forEach(e => {
      const k = e.Category || 'Other';
      if (!m.has(k)) m.set(k, { category: k, rows: [], total: 0, paid: 0, balance: 0 });
      const g = m.get(k);
      g.rows.push(e);
      if (!e.quotation) { g.total += e.total; g.paid += e.paid; g.balance += e.balance; }
      else g.quoted = round2((g.quoted || 0) + e.total);
    });
    const order = (c) => { const i = EXPENSE_CATEGORIES.indexOf(c); return i < 0 ? 999 : i; };
    return [...m.values()].sort((a, b) => order(a.category) - order(b.category) || a.category.localeCompare(b.category));
  }, [filtered]);

  // Everything owed - quotations left out, because three quotations for one
  // function would otherwise add a lakh to the balance. They are counted on
  // their own line (quoted / up to) so they are not forgotten either.
  const totals = useMemo(() => rows.filter(e => !e.quotation).reduce((t, e) => ({
    total: t.total + e.total, paid: t.paid + e.paid, balance: t.balance + e.balance,
    estimates: t.estimates + (Number(e.IsEstimate) === 1 ? 1 : 0), count: t.count + 1,
    dueSoon: t.dueSoon + (e.dueSoon ? e.balance : 0), dueSoonCount: t.dueSoonCount + (e.dueSoon ? 1 : 0),
    notOrdered: t.notOrdered + (!e.fromService && (e.Status || 'Planned') === 'Planned' ? 1 : 0),
  }), { total: 0, paid: 0, balance: 0, estimates: 0, count: 0, dueSoon: 0, dueSoonCount: 0, notOrdered: 0 }), [rows]);

  // "3 quotations · 62,500 · up to 1,17,400 if everything is taken."
  const quotes = useMemo(() => rows.filter(e => e.quotation).reduce((t, e) => ({
    count: t.count + 1,
    total: round2(t.total + e.total),
    upTo: round2(t.upTo + (e.money ? e.money.upTo : e.total)),
  }), { count: 0, total: 0, upTo: 0 }), [rows]);

  const categories = useMemo(() => [...new Set([...EXPENSE_CATEGORIES, ...rows.map(e => e.Category).filter(Boolean)])], [rows]);
  const paidByOptions = useMemo(() => [...new Set([myName, ...payments.map(p => p.PaidBy)].filter(Boolean))].sort(), [payments, myName]);

  // ── Expense add / edit / delete ──────────────────────────────────────────
  const openAdd = (category = '') => setForm({ ...blankExpense(), Category: category || catFilter || '' });
  const openEdit = (e) => setForm({
    ExpenseId: e.ExpenseId, Category: e.Category || '', Title: e.Title || '', FunctionId: e.FunctionId ? String(e.FunctionId) : '',
    VendorName: e.VendorName || '', VendorMobile: e.VendorMobile || '', AmountType: e.itemised ? 'Lines' : (e.AmountType || 'Fixed'),
    Rate: e.Rate ? String(Number(e.Rate)) : '', Persons: e.Persons ? String(e.Persons) : '',
    PersonsSource: e.PersonsSource || 'Manual', PersonsFunctionId: e.PersonsFunctionId ? String(e.PersonsFunctionId) : '',
    TotalAmount: e.TotalAmount ? String(Number(e.TotalAmount)) : '', IsEstimate: Number(e.IsEstimate) === 1 ? 1 : 0, Notes: e.Notes || '',
    Status: e.Status || 'Planned', NeededBy: e.NeededBy ? String(e.NeededBy).slice(0, 10) : '',
    DueDate: e.DueDate ? String(e.DueDate).slice(0, 10) : '',
    lines: (e.lines || []).map(lineToForm),
  });
  const closeForm = () => { if (!saving) setForm(null); };
  const setF = (k, v) => setForm(f => {
    let val = v;
    if (k === 'Title' || k === 'VendorName') val = normaliseText(v);
    if (k === 'VendorMobile') val = tenDigits(v);
    if (['Rate', 'TotalAmount'].includes(k)) val = String(v).replace(/[^\d.]/g, '');
    if (k === 'Persons') val = String(v).replace(/\D/g, '');
    const next = { ...f, [k]: val };
    // The food of a function: default the headcount to that function.
    if (k === 'FunctionId' && val && !f.PersonsFunctionId) next.PersonsFunctionId = val;
    // Itemising starts with one line carrying what the form already says.
    if (k === 'AmountType' && val === 'Lines' && !(f.lines || []).length) {
      next.lines = [blankLine({ Title: f.Title || '', FunctionId: f.FunctionId || '' })];
    }
    return next;
  });
  // The lines of an itemised expense.
  const setLine = (key, k, v) => setForm(f => ({
    ...f,
    lines: f.lines.map(l => {
      if (l.key !== key) return l;
      let val = v;
      if (k === 'Title' || k === 'Category') val = normaliseText(v);
      if (['Quantity', 'Rate', 'Amount'].includes(k)) val = String(v).replace(/[^\d.]/g, '');
      if (k === 'Days') val = String(v).replace(/\D/g, '');
      const next = { ...l, [k]: val };
      // A count that belongs to a function takes the expense's function.
      if (k === 'LiveSource' && val && val !== 'Cards' && !l.FunctionId) next.FunctionId = f.FunctionId || '';
      return next;
    }),
  }));
  const addLine = (over = {}) => setForm(f => {
    const last = f.lines[f.lines.length - 1];
    // A new line repeats the dates and function of the one above (rooms come in a row).
    const carry = last
      ? { FunctionId: last.FunctionId, FromDate: last.FromDate, ToDate: last.ToDate, Category: last.Category }
      : { FunctionId: f.FunctionId };
    return { ...f, lines: [...f.lines, blankLine({ ...carry, ...over })] };
  });
  const removeLine = (key) => setForm(f => ({ ...f, lines: f.lines.filter(l => l.key !== key) }));
  const lineInfo = (l) => lineMath(l, count);
  const formTotal = form ? totalOf(form) : 0;
  const formPersons = form ? personsOf(form) : 0;

  const saveExpense = async () => {
    const f = form;
    if (!String(f.Category).trim()) { toast('Choose a category'); return; }
    if (!String(f.Title).trim()) { toast('What is this expense for?'); return; }
    if (f.AmountType === 'PerPerson' && !num(f.Rate)) { toast('Enter the rate per person'); return; }
    if (f.AmountType === 'PerPerson' && f.PersonsSource === 'Headcount' && !f.PersonsFunctionId) { toast('Which function’s headcount?'); return; }
    if (f.VendorMobile && !/^\d{10}$/.test(f.VendorMobile)) { toast('Vendor mobile must be 10 digits'); return; }
    const itemised = f.AmountType === 'Lines';
    const named = (f.lines || []).filter(l => String(l.Title || '').trim());
    if (itemised && !named.length) { toast('Add at least one line'); return; }
    const badLine = named.find(l => l.Basis !== 'Fixed' && l.LiveSource && l.LiveSource !== 'Cards' && !l.FunctionId);
    if (itemised && badLine) { toast(`"${badLine.Title}": which function's count?`); return; }
    const data = {
      Category: normaliseText(f.Category).trim(), Title: f.Title.trim(), FunctionId: f.FunctionId ? Number(f.FunctionId) : null,
      VendorName: f.VendorName.trim() || null, VendorMobile: f.VendorMobile || null, AmountType: f.AmountType,
      Rate: f.AmountType === 'PerPerson' ? num(f.Rate) : 0,
      Persons: f.AmountType === 'PerPerson' && f.PersonsSource === 'Manual' ? num(f.Persons) : 0,
      PersonsSource: f.AmountType === 'PerPerson' ? f.PersonsSource : 'Manual',
      PersonsFunctionId: f.AmountType === 'PerPerson' && f.PersonsSource === 'Headcount' ? Number(f.PersonsFunctionId) : null,
      // A per-person total is stored as computed now (the list recomputes it live).
      TotalAmount: totalOf(f), IsEstimate: f.IsEstimate ? 1 : 0, Notes: f.Notes.trim() || null, UpdatedBy: me,
      Status: f.Status || 'Planned', NeededBy: f.NeededBy || null, DueDate: f.DueDate || null,
    };
    setSaving(true);
    try {
      let id = f.ExpenseId;
      if (id) await UpdateExpense({ data, where: `ExpenseId = ${Number(id)}` });
      else {
        const r = await AddExpense({ ...data, EventId: event.EventId, OrganiserId: event.OrganiserId, Valid: 1, CreatedBy: me });
        id = r?.insertId || r?.ExpenseId;
      }
      // Lines are saved after their expense (a new one needs its id first);
      // switching an itemised expense back to one amount retires its lines.
      const saved = linesFor('Expense', id);
      if (id && (itemised || saved.length)) {
        await saveLines({
          api: { add: AddCostLine, update: UpdateCostLine }, lines: itemised ? f.lines : [], saved,
          parentType: 'Expense', parentId: id, eventId: event.EventId, count, me,
        });
      }
      toast('Expense saved'); setForm(null); load(); reloadLines();
    } catch { toast('Could not save the expense'); } finally { setSaving(false); }
  };

  const deleteExpense = async (e) => {
    if (!window.confirm(`Delete "${e.Title}"${e.payments.length ? ` and its ${e.payments.length} payment(s)` : ''}?`)) return;
    try {
      await UpdateExpense({ data: { Valid: 0, UpdatedBy: me }, where: `ExpenseId = ${Number(e.ExpenseId)}` });
      if (e.payments.length) await UpdateExpensePayment({ data: { Valid: 0, UpdatedBy: me }, where: `ExpenseId = ${Number(e.ExpenseId)}` });
      if (e.lines.length) await UpdateCostLine({ data: { Valid: 0, UpdatedBy: me }, where: `ParentType = 'Expense' and ParentId = ${Number(e.ExpenseId)}` });
      toast('Deleted'); load();
    } catch { toast('Could not delete'); }
  };

  // ── Payments ─────────────────────────────────────────────────────────────
  const toggleOpen = (e) => setOpenId(id => (id === e.ExpenseId ? null : e.ExpenseId));
  const openPayAdd = (e) => {
    setOpenId(e.ExpenseId);
    // Default amount = what is still due.
    setPayForm({ ...blankPayment(e.ExpenseId, myName), Amount: e.balance > 0 ? String(e.balance) : '', ServiceId: e.fromService ? e.ServiceId : null });
  };
  const openPayEdit = (p) => setPayForm({
    ExpensePaymentId: p.ExpensePaymentId, ExpenseId: p.ExpenseId, PaymentDate: String(p.PaymentDate || '').slice(0, 10),
    ServiceId: p.ServiceId || null, PaymentId: p.PaymentId || null,
    Amount: String(Number(p.Amount || 0)), PaidBy: p.PaidBy || '', PaymentMode: p.PaymentMode || 'Cash', Notes: p.Notes || '',
  });
  const closePay = () => { if (!saving) setPayForm(null); };
  const setPF = (k, v) => setPayForm(f => ({
    ...f, [k]: k === 'Amount' ? String(v).replace(/[^\d.]/g, '') : k === 'PaidBy' ? normaliseText(v) : v,
  }));
  const savePayment = async () => {
    const f = payForm;
    if (!f.PaymentDate) { toast('Pick the payment date'); return; }
    if (!(num(f.Amount) > 0)) { toast('Enter the amount'); return; }
    const data = {
      PaymentDate: f.PaymentDate, Amount: num(f.Amount), PaidBy: String(f.PaidBy).trim() || null,
      PaymentMode: f.PaymentMode || null, Notes: String(f.Notes).trim() || null, UpdatedBy: me,
    };
    setSaving(true);
    try {
      if (f.ServiceId) {
        // A Services-page engagement: its own payment table.
        const sdata = {
          PaymentDate: data.PaymentDate, Amount: data.Amount, PaymentMode: data.PaymentMode,
          Remarks: [data.PaidBy ? `Paid by ${data.PaidBy}` : '', data.Notes || ''].filter(Boolean).join(' · ') || null,
          UpdatedBy: me,
        };
        if (f.PaymentId) await UpdateServicePayment({ data: sdata, where: `PaymentId = ${Number(f.PaymentId)}` });
        else await AddServicePayment({ ...sdata, ServiceId: Number(f.ServiceId), EventId: event.EventId, Valid: 1, CreatedBy: me });
        toast('Payment saved'); setPayForm(null); loadServices();
        return;
      }
      if (f.ExpensePaymentId) await UpdateExpensePayment({ data, where: `ExpensePaymentId = ${Number(f.ExpensePaymentId)}` });
      else await AddExpensePayment({ ...data, ExpenseId: Number(f.ExpenseId), EventId: event.EventId, Valid: 1, CreatedBy: me });
      toast('Payment saved'); setPayForm(null); load();
    } catch { toast('Could not save the payment'); } finally { setSaving(false); }
  };
  const deletePayment = async (p) => {
    if (!window.confirm(`Delete the payment of ₹${Number(p.Amount).toLocaleString('en-IN')}?`)) return;
    try {
      if (p.ServiceId) {
        // Soft delete; the API re-totals the service (AdvancePaid / balance).
        await UpdateServicePayment({ data: { Valid: 0, UpdatedBy: me }, where: `PaymentId = ${Number(p.PaymentId)}` });
        toast('Payment deleted'); loadServices();
        return;
      }
      await UpdateExpensePayment({ data: { Valid: 0, UpdatedBy: me }, where: `ExpensePaymentId = ${Number(p.ExpensePaymentId)}` });
      toast('Payment deleted'); load();
    } catch { toast('Could not delete'); }
  };

  return {
    canView, canAdd, canEdit, canDelete, loading, event, ownGuestsOnly: !isAdmin,
    rows, filtered, groups, totals, quotes, categories, functions, fnName, headcountOf, paidByOptions,
    q, setQ, catFilter, setCatFilter, onlyDue, setOnlyDue, stage, setStage, statuses: EXPENSE_STATUSES,
    openId, toggleOpen, openService,
    form, formTotal, formPersons, openAdd, openEdit, closeForm, setF, saveExpense, deleteExpense,
    setLine, addLine, removeLine, lineInfo, count,
    payForm, openPayAdd, openPayEdit, closePay, setPF, savePayment, deletePayment, saving,
  };
}
